<template>
  <div class="cookie-view">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">
            Cookie Policy
          </h1>
          <p class="hero-subtitle">
            How We Use Cookies & Similar Technologies
          </p>
          <div class="hero-badge">
            <i class="fas fa-cookie-bite mr-2" />
            Cookie Policy
          </div>
        </div>
      </div>
    </section>

    <!-- Table of Contents -->
    <section class="toc-section">
      <div class="container">
        <div class="toc-card">
          <h2 class="toc-title">
            Table of Contents
          </h2>
          <div class="toc-grid">
            <a
              v-for="(section, index) in tableOfContents"
              :key="index"
              :href="`#section-${index + 1}`"
              class="toc-item"
              @click="scrollToSection(index + 1)"
            >
              <span class="toc-number">{{ index + 1 }}</span>
              <span class="toc-text">{{ section }}</span>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Cookie Content -->
    <section class="cookie-content">
      <div class="container">
        <div class="content-wrapper">
          <!-- Section 1: What Are Cookies -->
          <div
            id="section-1"
            class="cookie-section"
          >
            <h2 class="section-title">
              1. What Are Cookies?
            </h2>
            <div class="cookie-content-text">
              <p>Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.</p>

              <p>Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your device when you go offline, while session cookies are deleted as soon as you close your web browser.</p>

              <div class="cookie-basics">
                <div class="cookie-basic-item">
                  <i class="fas fa-file-alt" />
                  <div class="basic-content">
                    <h4>Text Files</h4>
                    <p>Small pieces of data stored by your browser</p>
                  </div>
                </div>

                <div class="cookie-basic-item">
                  <i class="fas fa-cog" />
                  <div class="basic-content">
                    <h4>Functionality</h4>
                    <p>Help websites remember your preferences and improve performance</p>
                  </div>
                </div>

                <div class="cookie-basic-item">
                  <i class="fas fa-shield-alt" />
                  <div class="basic-content">
                    <h4>Safe</h4>
                    <p>Cannot contain viruses or install malware on your device</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 2: Types of Cookies We Use -->
          <div
            id="section-2"
            class="cookie-section"
          >
            <h2 class="section-title">
              2. Types of Cookies We Use
            </h2>
            <div class="cookie-content-text">
              <p>We use different types of cookies to enhance your experience on our platform:</p>

              <div class="cookie-types">
                <div class="cookie-type essential">
                  <div class="cookie-type-header">
                    <i class="fas fa-lock" />
                    <h3>Essential Cookies</h3>
                    <span class="cookie-status required">Required</span>
                  </div>
                  <p>These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services.</p>

                  <div class="cookie-examples">
                    <h4>Examples:</h4>
                    <ul>
                      <li>User authentication and login sessions</li>
                      <li>Security tokens and CSRF protection</li>
                      <li>Shopping cart functionality</li>
                      <li>Form submission data</li>
                      <li>Privacy preference settings</li>
                    </ul>
                  </div>
                </div>

                <div class="cookie-type functional">
                  <div class="cookie-type-header">
                    <i class="fas fa-wrench" />
                    <h3>Functional Cookies</h3>
                    <span class="cookie-status optional">Optional</span>
                  </div>
                  <p>These cookies enable enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages.</p>

                  <div class="cookie-examples">
                    <h4>Examples:</h4>
                    <ul>
                      <li>Language and region preferences</li>
                      <li>Theme selection (dark/light mode)</li>
                      <li>Customized dashboard layouts</li>
                      <li>Recently viewed content</li>
                      <li>Auto-fill form data</li>
                    </ul>
                  </div>
                </div>

                <div class="cookie-type analytics">
                  <div class="cookie-type-header">
                    <i class="fas fa-chart-bar" />
                    <h3>Analytics Cookies</h3>
                    <span class="cookie-status optional">Optional</span>
                  </div>
                  <p>These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.</p>

                  <div class="cookie-examples">
                    <h4>Examples:</h4>
                    <ul>
                      <li>Google Analytics tracking</li>
                      <li>Page view statistics</li>
                      <li>User behavior patterns</li>
                      <li>Feature usage metrics</li>
                      <li>Performance monitoring</li>
                    </ul>
                  </div>
                </div>

                <div class="cookie-type marketing">
                  <div class="cookie-type-header">
                    <i class="fas fa-bullhorn" />
                    <h3>Marketing Cookies</h3>
                    <span class="cookie-status optional">Optional</span>
                  </div>
                  <p>These cookies track your online activity to help advertisers deliver more relevant advertising or to limit how many times you see an ad.</p>

                  <div class="cookie-examples">
                    <h4>Examples:</h4>
                    <ul>
                      <li>Social media integration</li>
                      <li>Targeted advertising</li>
                      <li>Conversion tracking</li>
                      <li>Retargeting campaigns</li>
                      <li>Cross-platform tracking</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 3: Third-Party Cookies -->
          <div
            id="section-3"
            class="cookie-section"
          >
            <h2 class="section-title">
              3. Third-Party Cookies
            </h2>
            <div class="cookie-content-text">
              <p>We use several third-party services that may set their own cookies when you visit our website:</p>

              <div class="third-party-services">
                <div class="service-item">
                  <div class="service-header">
                    <i class="fab fa-google service-icon" />
                    <h4>Google Analytics</h4>
                  </div>
                  <p><strong>Purpose:</strong> Website analytics and performance monitoring</p>
                  <p><strong>Data Collected:</strong> Page views, user interactions, device information</p>
                  <p>
                    <strong>Privacy Policy:</strong> <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                    >Google Privacy Policy</a>
                  </p>
                </div>

                <div class="service-item">
                  <div class="service-header">
                    <i class="fab fa-stripe service-icon" />
                    <h4>Stripe</h4>
                  </div>
                  <p><strong>Purpose:</strong> Payment processing and fraud prevention</p>
                  <p><strong>Data Collected:</strong> Payment information, transaction details</p>
                  <p>
                    <strong>Privacy Policy:</strong> <a
                      href="https://stripe.com/privacy"
                      target="_blank"
                    >Stripe Privacy Policy</a>
                  </p>
                </div>

                <div class="service-item">
                  <div class="service-header">
                    <i class="fab fa-facebook service-icon" />
                    <h4>Facebook Pixel</h4>
                  </div>
                  <p><strong>Purpose:</strong> Social media integration and advertising</p>
                  <p><strong>Data Collected:</strong> Page visits, user actions, conversion events</p>
                  <p>
                    <strong>Privacy Policy:</strong> <a
                      href="https://www.facebook.com/privacy/explanation"
                      target="_blank"
                    >Facebook Privacy Policy</a>
                  </p>
                </div>

                <div class="service-item">
                  <div class="service-header">
                    <i class="fab fa-google service-icon" />
                    <h4>Google Tag Manager</h4>
                  </div>
                  <p><strong>Purpose:</strong> Tag and script management</p>
                  <p><strong>Data Collected:</strong> Website interactions, conversion tracking</p>
                  <p>
                    <strong>Privacy Policy:</strong> <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                    >Google Privacy Policy</a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 4: Cookie Management -->
          <div
            id="section-4"
            class="cookie-section"
          >
            <h2 class="section-title">
              4. How to Manage Cookies
            </h2>
            <div class="cookie-content-text">
              <p>You have several options for managing cookies on our website:</p>

              <div class="management-options">
                <div class="management-option">
                  <div class="option-header">
                    <i class="fas fa-cog" />
                    <h4>Cookie Preferences</h4>
                  </div>
                  <p>Use our cookie preference center to choose which types of cookies you want to accept. You can access this at any time through the cookie banner or footer link.</p>
                  <button
                    class="preference-button"
                    @click="openCookiePreferences"
                  >
                    <i class="fas fa-cookie-bite mr-2" />
                    Manage Cookie Preferences
                  </button>
                </div>

                <div class="management-option">
                  <div class="option-header">
                    <i class="fas fa-browser" />
                    <h4>Browser Settings</h4>
                  </div>
                  <p>Most web browsers allow you to control cookies through their settings preferences. You can set your browser to block or alert you about cookies.</p>

                  <div class="browser-links">
                    <a
                      href="https://support.google.com/chrome/answer/95647"
                      target="_blank"
                      class="browser-link"
                    >
                      <i class="fab fa-chrome" />
                      Chrome
                    </a>
                    <a
                      href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
                      target="_blank"
                      class="browser-link"
                    >
                      <i class="fab fa-firefox" />
                      Firefox
                    </a>
                    <a
                      href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
                      target="_blank"
                      class="browser-link"
                    >
                      <i class="fab fa-safari" />
                      Safari
                    </a>
                    <a
                      href="https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies"
                      target="_blank"
                      class="browser-link"
                    >
                      <i class="fab fa-edge" />
                      Edge
                    </a>
                  </div>
                </div>

                <div class="management-option">
                  <div class="option-header">
                    <i class="fas fa-mobile-alt" />
                    <h4>Mobile Devices</h4>
                  </div>
                  <p>For mobile devices, you can manage cookies through your device's privacy settings or browser preferences.</p>

                  <div class="mobile-instructions">
                    <div class="mobile-instruction">
                      <i class="fab fa-apple" />
                      <div>
                        <strong>iOS:</strong> Settings > Safari > Privacy & Security
                      </div>
                    </div>
                    <div class="mobile-instruction">
                      <i class="fab fa-android" />
                      <div>
                        <strong>Android:</strong> Chrome app > Settings > Privacy and Security
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="important-note">
                <i class="fas fa-exclamation-triangle" />
                <div class="note-content">
                  <h4>Important Note</h4>
                  <p>Blocking or deleting cookies may impact your ability to use certain features of our website. Essential cookies cannot be disabled as they are necessary for the website to function properly.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 5: Cookie Consent -->
          <div
            id="section-5"
            class="cookie-section"
          >
            <h2 class="section-title">
              5. Cookie Consent
            </h2>
            <div class="cookie-content-text">
              <p>When you first visit our website, you'll see a cookie banner asking for your consent to use non-essential cookies. You can:</p>

              <div class="consent-options">
                <div class="consent-option">
                  <i class="fas fa-check-circle" />
                  <div class="consent-content">
                    <h4>Accept All</h4>
                    <p>Allow all types of cookies for the best user experience</p>
                  </div>
                </div>

                <div class="consent-option">
                  <i class="fas fa-sliders-h" />
                  <div class="consent-content">
                    <h4>Customize</h4>
                    <p>Choose which categories of cookies you want to accept</p>
                  </div>
                </div>

                <div class="consent-option">
                  <i class="fas fa-times-circle" />
                  <div class="consent-content">
                    <h4>Reject Non-Essential</h4>
                    <p>Only allow essential cookies required for basic functionality</p>
                  </div>
                </div>
              </div>

              <p>You can change your cookie preferences at any time by clicking the "Cookie Settings" link in our website footer or by using the button provided in this policy.</p>
            </div>
          </div>

          <!-- Section 6: Data Retention -->
          <div
            id="section-6"
            class="cookie-section"
          >
            <h2 class="section-title">
              6. How Long Do Cookies Last?
            </h2>
            <div class="cookie-content-text">
              <p>Different cookies have different lifespans:</p>

              <div class="retention-table">
                <div class="retention-row header">
                  <div class="retention-cell">
                    Cookie Type
                  </div>
                  <div class="retention-cell">
                    Duration
                  </div>
                  <div class="retention-cell">
                    Description
                  </div>
                </div>

                <div class="retention-row">
                  <div class="retention-cell">
                    Session Cookies
                  </div>
                  <div class="retention-cell">
                    Until browser closes
                  </div>
                  <div class="retention-cell">
                    Automatically deleted when you close your browser
                  </div>
                </div>

                <div class="retention-row">
                  <div class="retention-cell">
                    Authentication
                  </div>
                  <div class="retention-cell">
                    30 days
                  </div>
                  <div class="retention-cell">
                    Keep you logged in for convenience
                  </div>
                </div>

                <div class="retention-row">
                  <div class="retention-cell">
                    Preferences
                  </div>
                  <div class="retention-cell">
                    1 year
                  </div>
                  <div class="retention-cell">
                    Remember your settings and preferences
                  </div>
                </div>

                <div class="retention-row">
                  <div class="retention-cell">
                    Analytics
                  </div>
                  <div class="retention-cell">
                    2 years
                  </div>
                  <div class="retention-cell">
                    Help us understand long-term usage patterns
                  </div>
                </div>

                <div class="retention-row">
                  <div class="retention-cell">
                    Marketing
                  </div>
                  <div class="retention-cell">
                    90 days
                  </div>
                  <div class="retention-cell">
                    Track advertising effectiveness
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 7: Updates to Cookie Policy -->
          <div
            id="section-7"
            class="cookie-section"
          >
            <h2 class="section-title">
              7. Changes to This Cookie Policy
            </h2>
            <div class="cookie-content-text">
              <p>We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons.</p>

              <p>When we make changes, we will:</p>

              <ul class="update-actions">
                <li>Update the "Last Modified" date at the bottom of this policy</li>
                <li>Notify you through our website banner or email if the changes are significant</li>
                <li>Ask for your renewed consent for any new types of cookies</li>
                <li>Provide clear information about what has changed</li>
              </ul>

              <p>We encourage you to review this Cookie Policy periodically to stay informed about how we use cookies.</p>
            </div>
          </div>

          <!-- Section 8: Contact Information -->
          <div
            id="section-8"
            class="cookie-section"
          >
            <h2 class="section-title">
              8. Contact Us
            </h2>
            <div class="cookie-content-text">
              <p>If you have any questions about our use of cookies or this Cookie Policy, please contact us:</p>

              <div class="contact-info">
                <div class="contact-method">
                  <i class="fas fa-envelope" />
                  <div class="contact-details">
                    <h4>Email</h4>
                    <p>support@indiefy.net</p>
                  </div>
                </div>

                <div class="contact-method">
                  <i class="fas fa-globe" />
                  <div class="contact-details">
                    <h4>Website</h4>
                    <p>www.indiefy.net</p>
                  </div>
                </div>

                <div class="contact-method">
                  <i class="fas fa-file-alt" />
                  <div class="contact-details">
                    <h4>Related Policies</h4>
                    <p>
                      <router-link to="/privacy-policy">
                        Privacy Policy
                      </router-link> |
                      <router-link to="/terms-and-conditions">
                        Terms & Conditions
                      </router-link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Last Updated Section -->
    <section class="last-updated-section">
      <div class="container">
        <div class="last-updated-card">
          <p>Last updated: <strong>{{ new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) }}</strong></p>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="contact-section">
      <div class="container">
        <div class="contact-card">
          <h3>Questions About Cookies?</h3>
          <p>If you have any questions about how we use cookies or need help managing your cookie preferences, our support team is here to help.</p>
          <a
            href="mailto:support@indiefy.net"
            class="contact-button"
          >
            <i class="fas fa-envelope mr-2" />
            Contact Support
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { usePageSeo } from '@/composables/usePageSeo';

usePageSeo({
  titleKey: 'cookie.seo-title',
  descriptionKey: 'cookie.seo-description',
});

const tableOfContents = [
  'What Are Cookies?',
  'Types of Cookies We Use',
  'Third-Party Cookies',
  'How to Manage Cookies',
  'Cookie Consent',
  'How Long Do Cookies Last?',
  'Changes to This Cookie Policy',
  'Contact Us',
];

const scrollToSection = (sectionNumber) => {
  const section = document.getElementById(`section-${sectionNumber}`);
  if (section) {
    section.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};

const openCookiePreferences = () => {
  // This would integrate with your cookie management system
  alert('Cookie preference center would open here');
};
</script>

<style lang="scss" scoped>
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/breakpoints";

.cookie-view {
  min-height: 100vh;
  background-color: var(--color-background, #fafafa);

  .body--dark & {
    background-color: var(--color-background, #0a0a0a);
  }
}

// Hero Section
.hero-section {
  background: linear-gradient(135deg, #f4a261 0%, #e9c46a 100%);
  padding: 5rem 1.5rem 3rem;
  color: white;
  text-align: center;

  .hero-title {
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1rem;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

    @media (max-width: $tablet) {
      font-size: 2.5rem;
    }

    @media (max-width: $mobile) {
      font-size: 2rem;
    }
  }

  .hero-subtitle {
    font-size: 1.5rem;
    font-weight: 400;
    margin-bottom: 2rem;
    opacity: 0.9;

    @media (max-width: $mobile) {
      font-size: 1.2rem;
    }
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.2);
    padding: 0.75rem 1.5rem;
    border-radius: 50px;
    font-weight: 600;
    backdrop-filter: blur(10px);
  }
}

// Table of Contents
.toc-section {
  padding: 3rem 1.5rem;
  background: var(--color-background, white);

  .body--dark & {
    background: var(--color-background, #0f0f0f);
  }
}

.toc-card {
  background: var(--color-card-background, white);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
  max-width: 800px;
  margin: 0 auto;

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }

  .toc-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-text, #333);
    margin-bottom: 2rem;
    text-align: center;
  }

  .toc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;

    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
    }
  }

  .toc-item {
    display: flex;
    align-items: center;
    padding: 1rem;
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;
    text-decoration: none;
    color: var(--color-text, #555);
    transition: all 0.3s ease;
    border: 2px solid transparent;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    &:hover {
      background: #fff3e0;
      border-color: #f4a261;
      transform: translateY(-2px);
      color: #e76f51;

      .body--dark & {
        background: #2d1b0a;
      }

      .toc-number {
        background: #f4a261;
        color: white;
      }
    }

    .toc-number {
      background: #e9c46a;
      color: white;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 0.9rem;
      margin-right: 1rem;
      transition: all 0.3s ease;
    }

    .toc-text {
      font-weight: 600;
      font-size: 0.95rem;
    }
  }
}

// Cookie Content
.cookie-content {
  padding: 3rem 1.5rem;
  background: var(--color-background, white);

  .body--dark & {
    background: var(--color-background, #0f0f0f);
  }
}

.content-wrapper {
  max-width: 900px;
  margin: 0 auto;
}

.cookie-section {
  margin-bottom: 4rem;
  background: var(--color-card-background, white);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 3rem;

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }

  .section-title {
    font-size: 2rem;
    font-weight: 700;
    color: #f4a261;
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 3px solid #e9c46a;
  }
}

// Cookie Basics
.cookie-basics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  .cookie-basic-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    i {
      font-size: 2rem;
      color: #f4a261;
    }

    .basic-content {
      h4 {
        color: #f4a261;
        font-weight: 700;
        margin-bottom: 0.5rem;
      }

      p {
        margin: 0;
        line-height: 1.6;
        font-size: 0.9rem;
      }
    }
  }
}

// Cookie Types
.cookie-types {
  .cookie-type {
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;
    padding: 2rem;
    margin-bottom: 2rem;
    border-left: 4px solid;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    &.essential {
      border-color: #e76f51;
    }

    &.functional {
      border-color: #2a9d8f;
    }

    &.analytics {
      border-color: #264653;
    }

    &.marketing {
      border-color: #f4a261;
    }

    .cookie-type-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
      flex-wrap: wrap;
      gap: 1rem;

      i {
        font-size: 1.5rem;
        margin-right: 0.5rem;
      }

      h3 {
        display: flex;
        align-items: center;
        margin: 0;
        font-weight: 700;
        flex: 1;
      }

      .cookie-status {
        padding: 0.25rem 0.75rem;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 600;

        &.required {
          background: #e76f51;
          color: white;
        }

        &.optional {
          background: #2a9d8f;
          color: white;
        }
      }
    }

    p {
      margin-bottom: 1.5rem;
      line-height: 1.6;
    }

    .cookie-examples {
      h4 {
        color: var(--color-text, #333);
        font-weight: 700;
        margin-bottom: 0.5rem;
      }

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          padding: 0.25rem 0;
          padding-left: 1.5rem;
          position: relative;
          line-height: 1.5;

          &::before {
            content: "•";
            color: #f4a261;
            font-weight: bold;
            position: absolute;
            left: 0;
          }
        }
      }
    }
  }
}

// Third Party Services
.third-party-services {
  .service-item {
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;
    padding: 2rem;
    margin-bottom: 2rem;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    .service-header {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;
      gap: 1rem;

      .service-logo {
        width: 32px;
        height: 32px;
      }

      .service-icon {
        font-size: 2rem;
        color: #f4a261;
      }

      h4 {
        color: #f4a261;
        font-weight: 700;
        margin: 0;
      }
    }

    p {
      margin-bottom: 0.5rem;
      line-height: 1.6;

      strong {
        color: var(--color-text, #333);
      }

      a {
        color: #f4a261;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

// Management Options
.management-options {
  .management-option {
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;
    padding: 2rem;
    margin-bottom: 2rem;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    .option-header {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;
      gap: 1rem;

      i {
        font-size: 1.5rem;
        color: #f4a261;
      }

      h4 {
        color: #f4a261;
        font-weight: 700;
        margin: 0;
      }
    }

    p {
      margin-bottom: 1.5rem;
      line-height: 1.6;
    }

    .preference-button {
      background: linear-gradient(135deg, #f4a261 0%, #e9c46a 100%);
      color: white;
      border: none;
      padding: 0.75rem 1.5rem;
      border-radius: 50px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(244, 162, 97, 0.3);
      }
    }

    .browser-links {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      gap: 1rem;

      .browser-link {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem;
        background: var(--color-background, white);
        border-radius: 8px;
        text-decoration: none;
        color: var(--color-text, #555);
        font-weight: 600;
        transition: all 0.3s ease;

        .body--dark & {
          background: var(--color-background, #1a1a1a);
        }

        &:hover {
          background: #fff3e0;
          color: #f4a261;

          .body--dark & {
            background: #2d1b0a;
          }
        }

        i {
          font-size: 1.2rem;
        }
      }
    }

    .mobile-instructions {
      .mobile-instruction {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 0.75rem;
        background: var(--color-background, white);
        border-radius: 8px;
        margin-bottom: 0.5rem;

        .body--dark & {
          background: var(--color-background, #1a1a1a);
        }

        i {
          font-size: 1.5rem;
          color: #f4a261;
        }
      }
    }
  }
}

// Important Note
.important-note {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: #fff3e0;
  border: 2px solid #f4a261;
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;

  .body--dark & {
    background: #2d1b0a;
  }

  i {
    color: #f4a261;
    font-size: 1.5rem;
    margin-top: 0.25rem;
  }

  .note-content {
    h4 {
      color: #f4a261;
      font-weight: 700;
      margin-bottom: 0.5rem;
    }

    p {
      margin: 0;
      line-height: 1.6;
    }
  }
}

// Consent Options
.consent-options {
  .consent-option {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;
    margin-bottom: 1.5rem;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    i {
      font-size: 2rem;
      color: #f4a261;
    }

    .consent-content {
      h4 {
        color: #f4a261;
        font-weight: 700;
        margin-bottom: 0.5rem;
      }

      p {
        margin: 0;
        line-height: 1.6;
      }
    }
  }
}

// Retention Table
.retention-table {
  background: var(--color-background-alt, #f8f9fa);
  border-radius: 12px;
  overflow: hidden;
  margin-top: 2rem;

  .body--dark & {
    background: var(--color-background, #2a2a2a);
  }

  .retention-row {
    display: grid;
    grid-template-columns: 1fr 1fr 2fr;
    gap: 1rem;

    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
      gap: 0.5rem;
    }

    &.header {
      background: #f4a261;
      color: white;

      .retention-cell {
        font-weight: 700;
        padding: 1rem;
      }
    }

    &:not(.header) {
      border-bottom: 1px solid var(--color-border, #e0e0e0);

      &:last-child {
        border-bottom: none;
      }

      .retention-cell {
        padding: 1rem;
      }
    }

    .retention-cell {
      display: flex;
      align-items: center;

      @media (max-width: $mobile) {
        &:not(:first-child)::before {
          content: attr(data-label) ": ";
          font-weight: 700;
          margin-right: 0.5rem;
        }
      }
    }
  }
}

// Update Actions
.update-actions {
  list-style: none;
  padding: 0;

  li {
    padding: 0.5rem 0;
    padding-left: 1.5rem;
    position: relative;

    &::before {
      content: "✓";
      color: #f4a261;
      font-weight: bold;
      position: absolute;
      left: 0;
    }
  }
}

// Contact Info
.contact-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  .contact-method {
    display: flex;
    align-items: flex-start;
    gap: 1rem;

    i {
      font-size: 1.5rem;
      color: #f4a261;
      margin-top: 0.25rem;
    }

    .contact-details {
      h4 {
        color: #f4a261;
        font-weight: 700;
        margin-bottom: 0.5rem;
      }

      p {
        margin: 0;
        line-height: 1.6;

        a {
          color: #f4a261;
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
}

// Last Updated Section
.last-updated-section {
  padding: 2rem 1.5rem;
  background: var(--color-background-alt, #f8f9fa);

  .body--dark & {
    background: var(--color-background-alt, #1a1a1a);
  }
}

.last-updated-card {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem;
  background: var(--color-card-background, white);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  p {
    margin: 0;
    font-size: 1.1rem;
    color: var(--color-text, #555);
  }
}

// Contact Section
.contact-section {
  padding: 3rem 1.5rem;
  background: var(--color-background-alt, #f8f9fa);

  .body--dark & {
    background: var(--color-background-alt, #1a1a1a);
  }
}

.contact-card {
  background: var(--color-background, white);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }

  h3 {
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-text, #333);
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    color: var(--color-text-light, #666);
    margin-bottom: 2rem;
    line-height: 1.6;
  }

  .contact-button {
    display: inline-flex;
    align-items: center;
    background: linear-gradient(135deg, #f4a261 0%, #e9c46a 100%);
    color: white;
    padding: 1rem 2rem;
    border-radius: 50px;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 16px rgba(244, 162, 97, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(244, 162, 97, 0.4);
      color: white;
    }
  }
}

// General Text Styling
.cookie-content-text {
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
    color: var(--color-text, #555);
  }

  strong {
    color: var(--color-text, #333);
  }
}
</style>